import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Reusable per-page SEO component: sets title, meta description, canonical URL,
 * Open Graph / Twitter card tags, optional JSON-LD structured data (single object or
 * array of schemas), and optional hreflang alternate links for FR/EN page pairs.
 *
 * IMPORTANT (Search Console "duplicate, Google chose different canonical" fix):
 * the canonical/og/hreflang URLs are ALWAYS built on the production domain (SITE_URL),
 * never on window.location.origin - otherwise the preview/staging domain publishes
 * exact duplicates of every page with their own self-canonicals, and Google may pick
 * the wrong canonical. Any host other than the production domain is also explicitly
 * noindexed.
 *
 * hreflang format: [{ lang: "fr", path: "/fr/..." }, { lang: "en", path: "/en/..." }]
 * "lang" may also be "x-default". Only pass pairs for pages that actually exist.
 */
export const SITE_URL = "https://kalypsomusic.com";

export default function SEO({ title, description, path = "/", jsonLd, ogImage, noindex = false, hreflang }) {
  const canonical = `${SITE_URL}${path}`;
  const image = ogImage || `${SITE_URL}/icon-512.png`;
  const jsonLdList = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const isProdHost = typeof window === "undefined" || window.location.hostname === "kalypsomusic.com";
  const blockIndexing = noindex || !isProdHost;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {blockIndexing && <meta name="robots" content="noindex, nofollow" />}
      {hreflang && hreflang.map((h) => <link key={h.lang} rel="alternate" hrefLang={h.lang} href={`${SITE_URL}${h.path}`} />)}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Kalypso Music" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {jsonLdList.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
}
